import { AlertCircle, ArrowRight, X } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export interface CrmValueOption {
    value: string;
    label: string;
}

interface CrmValueMappingProps {
    /** Provider display name for copy, e.g. "HubSpot" or "Salesforce". */
    providerName: string;
    /** Options configured on the Aimdoc dropdown/select field. */
    fieldOptions: string[];
    /** Options on the mapped CRM enumeration/picklist field. */
    crmOptions: CrmValueOption[];
    /** Explicit Aimdoc option -> CRM internal value mapping. */
    mapping: Record<string, string>;
    onChange: (next: Record<string, string>) => void;
}

const AUTO_VALUE = '__auto__';

/** Mirrors the sync-time fuzzy fallback: exact value, exact label, then case-insensitive. */
export function findAutoMatch(option: string, crmOptions: CrmValueOption[]): CrmValueOption | null {
    const trimmed = option.trim();
    const byValue = crmOptions.find(o => o.value === trimmed);
    if (byValue) return byValue;
    const byLabel = crmOptions.find(o => o.label === trimmed);
    if (byLabel) return byLabel;
    const lower = trimmed.toLowerCase();
    return crmOptions.find(
        o => o.value?.toLowerCase() === lower || o.label?.toLowerCase() === lower
    ) || null;
}

export function CrmValueMapping({
    providerName,
    fieldOptions,
    crmOptions,
    mapping,
    onChange,
}: CrmValueMappingProps) {
    const selectableOptions = crmOptions.filter(o => o.value !== '' && o.value != null);
    const staleOptions = Object.keys(mapping || {}).filter(opt => !fieldOptions.includes(opt));

    const handleSelect = (fieldOption: string, selected: string) => {
        const next = { ...mapping };
        if (selected === AUTO_VALUE) {
            delete next[fieldOption];
        } else {
            next[fieldOption] = selected;
        }
        onChange(next);
    };

    const removeStale = (fieldOption: string) => {
        const next = { ...mapping };
        delete next[fieldOption];
        onChange(next);
    };

    return (
        <div className="rounded-lg border bg-background p-3 space-y-2">
            <div>
                <div className="text-xs font-medium">Option Values</div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                    Choose the {providerName} value each option syncs as. "Auto" matches options
                    to {providerName} values by name.
                </p>
            </div>
            <div className="space-y-1.5">
                {fieldOptions.map((option) => {
                    const explicit = mapping?.[option];
                    const autoMatch = findAutoMatch(option, selectableOptions);
                    const unresolved = !explicit && !autoMatch;
                    return (
                        <div key={option} className="flex items-center gap-2">
                            <div className="flex-1 min-w-0 flex items-center gap-1.5">
                                <span className="text-xs truncate" title={option}>{option}</span>
                                {unresolved && (
                                    <span
                                        className="inline-flex flex-shrink-0 items-center"
                                        title={`No matching ${providerName} value — the raw option text will be sent and ${providerName} may reject it. Pick a value to fix this.`}
                                    >
                                        <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                                    </span>
                                )}
                            </div>
                            <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <Select
                                    value={explicit || AUTO_VALUE}
                                    onValueChange={(selected) => handleSelect(option, selected)}
                                >
                                    <SelectTrigger className="h-8 text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="z-[70]">
                                        <SelectItem value={AUTO_VALUE} className="text-xs">
                                            {autoMatch
                                                ? `Auto · ${autoMatch.label}`
                                                : 'Auto · no match'}
                                        </SelectItem>
                                        {selectableOptions.map((crmOption) => (
                                            <SelectItem
                                                key={crmOption.value}
                                                value={crmOption.value}
                                                className="text-xs"
                                            >
                                                {crmOption.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    );
                })}
            </div>
            {staleOptions.length > 0 && (
                <div className="pt-1 space-y-1.5">
                    <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                        <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-px" />
                        <span>
                            These mapped options no longer exist on the field (renamed or removed):
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {staleOptions.map((option) => (
                            <Badge key={option} variant="secondary" className="gap-1 text-[11px]">
                                <span className="truncate max-w-[160px]">{option}</span>
                                <button
                                    type="button"
                                    className="ml-0.5 rounded hover:bg-muted px-0.5"
                                    onClick={() => removeStale(option)}
                                    aria-label={`Remove stale value mapping for ${option}`}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
